import { ASSISTANT, CHOICE, CONTACT, DOCUMENT, ESCALATE, FORM, HANDOFF, LOOKUP, SUBMIT } from './nodes.js';
import { ESCALATION_ENTRY, ESCALATION_NODES } from './escalation.js';
import Services from '../Services.js';

/*
 * Flow
 *
 * The walker. Pure functions over a plain state object: every transition takes
 * a state and returns a new one, and nothing here touches React, the DOM, or
 * the network.
 *
 * That is what makes the graph testable: a whole brand tree can be walked end
 * to end in node, asserting the path and the resulting payload, with no
 * rendering involved. The renderers in P3 are then only responsible for drawing
 * a node and reporting an answer.
 *
 * TWO GRAPHS, ONE WALKER. `phase` says which one we are in: the brand's own
 * tree, or the shared escalation sub-flow. Escalating does not leave the flow;
 * it changes which graph the same state machine is walking, which is why the
 * answers and the path survive the transition.
 */

const MAX_GUARD_HOPS = 20;
const emptyState = () => ({
  answers: {},
  brandId: null,
  documentsShown: [],
  escalation: null,
  guardsFired: [],
  history: [],
  lookup: null,
  nodeId: null,
  phase: 'tree'
});

// A guard is identified by where it fires from and where it sends you, so the
// same advisory reached from two different nodes is two different diversions.
const guardKey = (from, guard) => `${from}>${guard.goto}`;

// ---------------------------------------------------------------------------
// facts

/*
 * Derived facts, recomputed after every answer.
 *
 * Age gates live here rather than in the trees. "Over 25 years" is written once
 * as a rule over an install year, not spelled out separately inside MasterGuard
 * and Homefront where the two would eventually disagree.
 */
export const deriveFacts = (state, brand) => {
  let facts = {
    // 'call', 'write', or null when nobody has been asked yet.
    channel: state.escalation?.channel || null,
    // Why they are escalating. Decides whether the assistant is offered.
    reason: state.escalation?.reason || null,
    hasDealership: Boolean(state.lookup?.dealership)
  };
  let year = parseInt(state.answers.installYear || state.answers.manufactureYear, 10);
  if (year > 0) {
    facts.year = year;
    facts.age = new Date().getFullYear() - year;
  }

  // A brand may add its own, and may read the ones above.
  Object.entries(brand?.derive || {}).forEach(([key, fn]) => {
    try {
      facts[key] = fn(state.answers, facts);
    } catch (e) {
      console.error(`derive "${key}" failed: ${e.message}`);
    }
  });
  return facts;
};

// ---------------------------------------------------------------------------
// graph access

const graphFor = (state, brand) => {
  return state.phase === 'escalation' ? ESCALATION_NODES : brand?.nodes || {};
};
export const nodeAt = (state, brand, id) => graphFor(state, brand)[id || state.nodeId] || null;

/*
 * Guards are evaluated on ENTERING a node, and may redirect.
 *
 * A guard is a standing rule by default: "this brand never looks up a dealership"
 * holds every time that node is entered, and should. But a guard that diverts
 * somebody to an ADVISORY is different. It is a thing to say once. Left
 * standing it becomes a trap: the customer reads that their units are past ten
 * years, presses "something else is wrong", returns to the menu, and the same
 * guard sends them straight back to the same advisory, forever.
 *
 * So the difference is declared rather than inferred. `once: true` marks the
 * one-time diversions, and a fired guard is remembered on the state.
 *
 * Hops are bounded regardless, so a pair of standing guards pointing at each
 * other cannot hang the page. It logs and stops, which is a visible bug rather
 * than a frozen tab.
 */
// Which guard on this node wants to redirect, if any. Lifted out of settle's
// loop so it closes over nothing that changes underneath it.
const firingGuard = (node, nodeId, fired, facts, brand, answers) => {
  return (node.guard || []).find(guard => {
    if (guard.once === true && fired.includes(guardKey(nodeId, guard))) {
      return false;
    }
    try {
      return guard.when(facts, brand, answers) === true;
    } catch (e) {
      console.error(`guard on "${nodeId}" failed: ${e.message}`);
      return false;
    }
  });
};
const settle = (state, brand, id) => {
  let current = id;
  let fired = state.guardsFired || [];
  for (let hop = 0; hop < MAX_GUARD_HOPS; hop++) {
    let node = graphFor(state, brand)[current];
    if (!node) {
      return {
        guardsFired: fired,
        nodeId: current
      };
    }
    let facts = deriveFacts(state, brand);
    let redirect = firingGuard(node, current, fired, facts, brand, state.answers);
    if (!redirect) {
      return {
        guardsFired: fired,
        nodeId: current
      };
    }
    if (redirect.once === true) {
      fired = fired.concat([guardKey(current, redirect)]);
    }
    current = redirect.goto;
  }
  console.error(`guards did not settle within ${MAX_GUARD_HOPS} hops from "${id}"`);
  return {
    guardsFired: fired,
    nodeId: current
  };
};

// ---------------------------------------------------------------------------
// transitions

/*
 * The PATH only records the brand tree.
 *
 * The escalation sub-flow asks for a name and a message, and both already
 * arrive on the payload as structured fields. Recording them a
 * second time as questions and answers would mean whoever picks the escalation
 * up reads the customer's email twice and has to work out which copy is
 * authoritative. The path answers exactly one question: what did the tree
 * already ask this person. It stops at the point they gave up on it.
 */
/*
 * Move to a new node, recording where we came FROM.
 *
 * `from` matters. escalate() and handoff() build the next state before calling
 * this, so by then `state` already carries the new phase or the new brand, and
 * a history frame taken from it would record where we are going rather than
 * where we have been.
 *
 * That was a real bug, and not a small one. Back out of the contact form and
 * the frame said phase "escalation" at a node id belonging to the brand tree,
 * so the lookup found nothing in either graph, the step rendered as null, and
 * the customer landed on the "we have not written this yet, talk to a person"
 * fallback. It looked like a clumsy Back button. It was the flow losing its
 * place entirely.
 */
const advance = (state, brand, id, entry, from = state) => {
  let settled = settle(state, brand, id);
  return {
    ...state,
    guardsFired: settled.guardsFired,
    history: state.history.concat([{
      brandId: from.brandId,
      nodeId: from.nodeId,
      phase: from.phase
    }]),
    nodeId: settled.nodeId,
    path: entry && from.phase === 'tree' ? (state.path || []).concat([entry]) : state.path
  };
};

/*
 * The one node type Back steps over.
 *
 * A lookup is a pause while we check a record. Landing on it going backwards
 * re-runs it and throws you forward again, so Back appears to do nothing.
 *
 * ESCALATE USED TO BE ON THIS LIST AND NO LONGER IS. It was a screen whose only
 * control was Continue, so there was nothing to come back to. Then the call and
 * write options moved onto it, and skipping it meant somebody who picked the
 * wrong one of the two could not get back to change their mind: Back stepped
 * straight over the question and out into the tree. A screen with a decision on
 * it is a screen Back has to be able to reach.
 */
const SKIP_ON_BACK = [LOOKUP];
const stepBack = state => {
  if (state.history.length === 0) {
    return state;
  }
  let previous = state.history[state.history.length - 1];
  return {
    ...state,
    brandId: previous.brandId,
    escalation: previous.phase === 'tree' ? null : state.escalation,
    history: state.history.slice(0, -1),
    nodeId: previous.nodeId,
    path: (state.path || []).slice(0, -1),
    phase: previous.phase
  };
};
const Flow = {
  /*
   * Begin a flow.
   *
   * `seed.entry` starts somewhere other than the top, which is how a link
   * that used to point at a standalone page can land on the part of the tree
   * that replaced it. Somebody following an old "mobile app support" link
   * arrives at the app questions rather than at "how can we help", which is
   * where they were going.
   *
   * An entry that does not exist falls back to the top rather than to a blank
   * screen, because a stale link should be a slightly longer route to the
   * answer, not a dead end.
   */
  start: (brand, seed = {}) => {
    let {
      entry,
      ...rest
    } = seed;
    let state = {
      ...emptyState(),
      ...rest,
      brandId: brand.id,
      path: []
    };
    let from = entry && brand.nodes?.[entry] ? entry : brand.entry;
    let settled = settle(state, brand, from);
    return {
      ...state,
      guardsFired: settled.guardsFired,
      nodeId: settled.nodeId
    };
  },
  current: (state, brand) => nodeAt(state, brand),
  /*
   * The options a choice should actually draw.
   *
   * An option may carry a `when`, which is how one shared node offers a
   * brand-specific route without the node being duplicated per brand: the
   * escalation asks everybody how they want to be reached, and only the brand
   * with an assistant is offered one.
   *
   * The validator still walks the hidden ones. An option nobody can currently
   * see is still an edge that has to point at a node that exists, and the day
   * a second brand gets an assistant is not the day to find out it does not.
   */
  options: (state, brand) => {
    let node = nodeAt(state, brand);
    let facts = deriveFacts(state, brand);
    return (node?.options || []).filter(option => {
      if (typeof option.when !== 'function') {
        return true;
      }
      try {
        return option.when(facts, brand, state.answers) === true;
      } catch (e) {
        console.error(`option "${option.label}" failed its condition: ${e.message}`);
        return false;
      }
    });
  },
  facts: (state, brand) => deriveFacts(state, brand),
  /*
   * Answer the current node.
   *
   * `value` is a field map for a form, the chosen option for a choice, and is
   * ignored by the rest. Returns the next state; the caller does not need to
   * know which kind of node it just answered.
   */
  answer: (state, brand, value) => {
    let node = nodeAt(state, brand);
    if (!node) {
      return state;
    }
    if (node.type === FORM) {
      let next = {
        ...state,
        answers: {
          ...state.answers,
          ...value
        }
      };
      let entry = {
        answer: node.fields.filter(field => value?.[field.id]).map(field => `${field.label}: ${value[field.id]}`).join(', '),
        nodeId: state.nodeId,
        question: node.title
      };
      return advance(next, brand, node.next, entry.answer ? entry : null);
    }
    if (node.type === CHOICE) {
      let option = value;
      let next = option?.value ? {
        ...state,
        answers: {
          ...state.answers,
          [node.id || state.nodeId]: option.value
        }
      } : state;
      let entry = {
        answer: option?.label,
        nodeId: state.nodeId,
        question: node.prompt
      };
      return advance(next, brand, option?.next, entry);
    }

    /*
     * A document's "something else is wrong". Nothing goes on the path:
     * the customer did not answer a question, they declined an answer, and
     * the document they declined is already recorded in documentsShown.
     */
    if (node.type === DOCUMENT || node.type === CONTACT || node.type === ASSISTANT) {
      return advance(state, brand, value?.next, null);
    }
    if (node.type === HANDOFF) {
      return state;
    }
    return state;
  },
  /*
   * The lookup result, fed back in by the caller. The engine stays pure: the
   * app performs the request, this records the outcome and moves on.
   */
  applyLookup: (state, brand, result) => {
    let next = {
      ...state,
      lookup: result || {
        dealership: null
      }
    };
    let node = nodeAt(next, brand);
    return advance(next, brand, node?.next, null);
  },
  /*
   * Hand off to another brand's tree, carrying everything gathered so far.
   * This is what makes Applied Fire a router rather than a sixth brand.
   */
  handoff: (state, nextBrand) => {
    let next = {
      ...state,
      brandId: nextBrand.id
    };
    return advance(next, nextBrand, nextBrand.entry, null, state);
  },
  /*
   * Enter the escalation sub-flow.
   *
   * All three ways in arrive here: a document's "this did not help", the
   * persistent contact action, and an explicit escalate node. Only the reason
   * differs, and whether they have already said how they want to be reached. Only the
   * reason differs.
   */
  escalate: (state, brand, reason, channel) => {
    let next = {
      ...state,
      escalation: {
        channel: channel || null,
        reason: reason || 'general'
      },
      phase: 'escalation'
    };
    return advance(next, brand, ESCALATION_ENTRY, null, state);
  },
  // A document was shown. Recorded so an escalation can say what was already
  // read and rejected, rather than having an agent reply with the same article.
  markDocumentShown: (state, id) => {
    return state.documentsShown.includes(id) ? state : {
      ...state,
      documentsShown: state.documentsShown.concat([id])
    };
  },
  canGoBack: state => state.history.length > 0,
  /*
   * Back, one answered question at a time.
   *
   * SOME NODES ARE NOT QUESTIONS AND ARE STEPPED STRAIGHT OVER. A lookup is a
   * pause while we check a record; an escalate node is the screen that says
   * "we will get a person on this" before asking for contact details. Neither
   * holds an answer anybody can change.
   *
   * Landing on one going backwards is worse than useless. The lookup re-runs
   * and throws you forward again, so Back appears to do nothing at all. The
   * escalate node is quieter but wronger: somebody backing out of the contact
   * form wants the question that sent them there, and instead gets a second
   * generic "let us get a person on this" screen, which reads like the app
   * has lost its place. Both are skipped until Back reaches something the
   * customer actually chose.
   *
   * `resolve` maps a brand id to a brand, because a handoff means the node we
   * are stepping back onto may belong to a different tree than the one we are
   * standing in. The engine holds no registry, so the caller supplies it.
   */
  back: (state, resolve) => {
    let next = stepBack(state);
    while (resolve && Flow.canGoBack(next)) {
      let node = nodeAt(next, resolve(next.brandId));
      if (!node || SKIP_ON_BACK.includes(node.type) === false) {
        break;
      }
      next = stepBack(next);
    }
    return next;
  },
  isComplete: (state, brand) => {
    let node = nodeAt(state, brand);
    return node?.type === SUBMIT;
  },
  /*
   * Everything an escalation needs, assembled from state.
   *
   * The PATH is the valuable part. Without it whoever picks this up re-asks
   * every question the customer already answered, and the tree bought nothing.
   */
  payload: (state, brand) => {
    let facts = deriveFacts(state, brand);
    let a = state.answers;
    return {
      brand: state.brandId,
      customer: {
        email_address: a.email_address?.trim(),
        first_name: a.firstName?.trim(),
        last_name: a.lastName?.trim(),
        phone_number: a.phone_number?.replace(/[^0-9]/g, '')
      },
      // Whose site this came in through, not who should act on it. The
      // server resolves the destination from the customer's details.
      dealership_id: Services.dealership()?.id || null,
      documents_shown: state.documentsShown,
      facts: facts,
      message: a.message?.trim() || null,
      path: (state.path || []).map(entry => ({
        answer: entry.answer,
        question: entry.question
      })),
      reason: state.escalation?.reason || 'general'
    };
  }
};
export default Flow;
export { ASSISTANT, CHOICE, CONTACT, DOCUMENT, ESCALATE, FORM, HANDOFF, LOOKUP, SUBMIT };